import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import FilterPanel from './components/FilterPanel';
import SearchHeader from './components/SearchHeader';
import ResultsGrid from './components/ResultsGrid';

const SearchResults = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Initialize filters from URL params or defaults
  const [filters, setFilters] = useState({
    query: searchParams?.get('q') || '',
    category: searchParams?.get('category') || 'all',
    location: searchParams?.get('location') || '',
    dateFrom: searchParams?.get('dateFrom') || '',
    dateTo: searchParams?.get('dateTo') || '',
    priceRange: searchParams?.get('priceRange') || 'all',
    deliveryType: searchParams?.get('deliveryType') || 'all',
    sortBy: searchParams?.get('sortBy') || 'relevance',
    ukWide: searchParams?.get('ukWide') === 'true'
  });

  // Mock listings data
  const mockListings = [
    {
      id: 1,
      name: "Mindful Morning Meditation",
      practitioner: "Sarah Johnson",
      businessLogo: "https://images.unsplash.com/photo-1544717297-fa95b6ee9643?w=100&h=100&fit=crop&crop=face",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      description: "Start your day with guided meditation sessions designed to bring clarity and peace to your morning routine.",
      location: "Hampstead, London",
      distance: 2.3,
      category: "mindfulness-meditation",
      categoryLabel: "Mindfulness & Meditation",
      tags: ["Beginner Friendly", "Morning Sessions"],
      price: 25,
      priceUnit: "session",
      nextDate: "2025-01-15",
      rating: 4.8,
      reviewCount: 127,
      featured: true,
      isOnline: false
    },
    {
      id: 2,
      name: "Vinyasa Flow Yoga",
      practitioner: "Marcus Chen",
      businessLogo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      image: "https://images.pexels.com/photos/3822622/pexels-photo-3822622.jpeg?w=400&h=300&fit=crop",
      description: "Dynamic yoga flows that connect breath with movement, suitable for intermediate practitioners.",
      location: "Camden, London",
      distance: 1.8,
      category: "yoga-movement",
      categoryLabel: "Yoga & Movement",
      tags: ["Intermediate", "Evening Classes"],
      price: 35,
      priceUnit: "class",
      nextDate: "2025-01-16",
      rating: 4.9,
      reviewCount: 89,
      featured: false,
      isOnline: true
    },
    {
      id: 3,
      name: "Reiki Healing Sessions",
      practitioner: "Emma Thompson",
      businessLogo: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face",
      image: "https://images.pixabay.com/photo/2017/08/07/14/02/people-2604149_1280.jpg?w=400&h=300&fit=crop",
      description: "Traditional Japanese healing technique for stress reduction and relaxation through gentle touch.",
      location: "Islington, London",
      distance: 3.1,
      category: "energy-healing",
      categoryLabel: "Energy Healing",
      tags: ["One-to-One", "Healing"],
      price: 80,
      priceUnit: "session",
      nextDate: "2025-01-17",
      rating: 4.7,
      reviewCount: 156,
      featured: false,
      isOnline: false
    },
    {
      id: 4,
      name: "Aromatherapy Massage",
      practitioner: "David Wilson",
      businessLogo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400&h=300&fit=crop",
      description: "Therapeutic massage using essential oils to promote physical and emotional wellbeing.",
      location: "Greenwich, London",
      distance: 5.2,
      category: "complementary-therapies",
      categoryLabel: "Complementary Therapies",
      tags: ["Relaxation", "Therapeutic"],
      price: 90,
      priceUnit: "session",
      nextDate: "2025-01-18",
      rating: 4.6,
      reviewCount: 203,
      featured: false,
      isOnline: false
    },
    {
      id: 5,
      name: "Nutritional Wellness Consultation",
      practitioner: "Dr. Lisa Parker",
      businessLogo: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100&h=100&fit=crop&crop=face",
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?w=400&h=300&fit=crop",
      description: "Personalised nutrition plans and lifestyle guidance for optimal health and vitality.",
      location: "Kensington, London",
      distance: 4.7,
      category: "nutrition-wellness",
      categoryLabel: "Nutrition & Wellness",
      tags: ["Consultation", "Personalised"],
      price: 120,
      priceUnit: "consultation",
      nextDate: "2025-01-19",
      rating: 4.9,
      reviewCount: 78,
      featured: true,
      isOnline: true
    },
    {
      id: 6,
      name: "Crystal Healing Workshop",
      practitioner: "Luna Martinez",
      businessLogo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
      image: "https://images.pixabay.com/photo/2018/01/17/20/22/analytics-3088958_1280.jpg?w=400&h=300&fit=crop",
      description: "Learn about crystal properties and healing techniques in this hands-on workshop experience.",
      location: "Shoreditch, London",
      distance: 2.9,
      category: "spirituality-esoterica",
      categoryLabel: "Spirituality & Esoterica",
      tags: ["Workshop", "Crystals"],
      price: 45,
      priceUnit: "workshop",
      nextDate: "2025-01-20",
      rating: 4.5,
      reviewCount: 92,
      featured: false,
      isOnline: false
    },
    {
      id: 7,
      name: "Sound Bath Meditation",
      practitioner: "James Anderson",
      businessLogo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop",
      description: "Immersive sound healing experience using Tibetan bowls, gongs, and other therapeutic instruments.",
      location: "Notting Hill, London",
      distance: 3.8,
      category: "sound-breathwork",
      categoryLabel: "Sound & Breathwork",
      tags: ["Group Session", "Relaxation"],
      price: 30,
      priceUnit: "session",
      nextDate: "2025-01-21",
      rating: 4.8,
      reviewCount: 134,
      featured: false,
      isOnline: false
    },
    {
      id: 8,
      name: "Life Coaching Sessions",
      practitioner: "Rachel Green",
      businessLogo: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face",
      image: "https://images.pexels.com/photos/7176026/pexels-photo-7176026.jpeg?w=400&h=300&fit=crop",
      description: "Personalised coaching to help you achieve your goals and unlock your full potential.",
      location: "Canary Wharf, London",
      distance: 6.1,
      category: "coaching-development",
      categoryLabel: "Coaching & Development",
      tags: ["One-to-One", "Goal Setting"],
      price: 150,
      priceUnit: "session",
      nextDate: "2025-01-22",
      rating: 4.9,
      reviewCount: 67,
      featured: true,
      isOnline: true
    },
    {
      id: 9,
      name: "Art Therapy Workshop",
      practitioner: "Sophie Turner",
      businessLogo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face",
      image: "https://images.pixabay.com/photo/2017/05/15/17/43/cup-2315554_1280.jpg?w=400&h=300&fit=crop",
      description: "Express yourself through creative art activities designed to promote emotional healing and self-discovery.",
      location: "Clapham, London",
      distance: 4.3,
      category: "creative-therapy",
      categoryLabel: "Creative Therapy",
      tags: ["Workshop", "Creative"],
      price: 55,
      priceUnit: "workshop",
      nextDate: "2025-01-23",
      rating: 4.7,
      reviewCount: 45,
      featured: false,
      isOnline: false
    },
    {
      id: 10,
      name: "Weekend Wellness Retreat",
      practitioner: "Mindful Escapes",
      businessLogo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      description: "Two-day retreat combining yoga, meditation, healthy meals, and nature walks in the countryside.",
      location: "Surrey Countryside",
      distance: 45.2,
      category: "retreats-immersions",
      categoryLabel: "Retreats & Immersions",
      tags: ["Weekend", "All-Inclusive"],
      price: 280,
      priceUnit: "retreat",
      nextDate: "2025-01-25",
      rating: 4.9,
      reviewCount: 112,
      featured: true,
      isOnline: false
    }
  ];

  // Filter listings based on current filters
  const filterListings = (listings, filters) => {
    return listings?.filter(listing => {
      // Query filter
      if (filters?.query) {
        const query = filters?.query?.toLowerCase();
        const searchableText = `${listing?.name} ${listing?.practitioner} ${listing?.description} ${listing?.tags?.join(' ')}`?.toLowerCase();
        if (!searchableText?.includes(query)) return false;
      }

      // Category filter
      if (filters?.category && filters?.category !== 'all') {
        if (listing?.category !== filters?.category) return false;
      }

      // Location filter
      if (filters?.location) {
        const location = filters?.location?.toLowerCase();
        if (!listing?.location?.toLowerCase()?.includes(location)) return false;
      }

      // Date filter
      if (filters?.dateFrom) {
        const listingDate = new Date(listing.nextDate);
        const fromDate = new Date(filters.dateFrom);
        if (listingDate < fromDate) return false;
      }

      if (filters?.dateTo) {
        const listingDate = new Date(listing.nextDate);
        const toDate = new Date(filters.dateTo);
        if (listingDate > toDate) return false;
      }

      // Price filter
      if (filters?.priceRange && filters?.priceRange !== 'all') {
        const price = listing?.price;
        switch (filters?.priceRange) {
          case '0-25':
            if (price > 25) return false;
            break;
          case '25-50':
            if (price < 25 || price > 50) return false;
            break;
          case '50-100':
            if (price < 50 || price > 100) return false;
            break;
          case '100-200':
            if (price < 100 || price > 200) return false;
            break;
          case '200+':
            if (price < 200) return false;
            break;
        }
      }

      // Delivery type filter
      if (filters?.deliveryType && filters?.deliveryType !== 'all') {
        if (filters?.deliveryType === 'online' && !listing?.isOnline) return false;
        if (filters?.deliveryType === 'in-person' && listing?.isOnline) return false;
      }

      return true;
    });
  };

  // Sort listings
  const sortListings = (listings, sortBy) => {
    const sorted = [...listings];
    
    switch (sortBy) {
      case 'distance':
        return sorted?.sort((a, b) => (a?.distance || 0) - (b?.distance || 0));
      case 'price-low':
        return sorted?.sort((a, b) => (a?.price || 0) - (b?.price || 0));
      case 'price-high':
        return sorted?.sort((a, b) => (b?.price || 0) - (a?.price || 0));
      case 'date-newest':
        return sorted?.sort((a, b) => new Date(b.nextDate) - new Date(a.nextDate));
      case 'date-oldest':
        return sorted?.sort((a, b) => new Date(a.nextDate) - new Date(b.nextDate));
      case 'relevance':
      default:
        // Sort by featured first, then by rating
        return sorted?.sort((a, b) => {
          if (a?.featured && !b?.featured) return -1;
          if (!a?.featured && b?.featured) return 1;
          return (b?.rating || 0) - (a?.rating || 0);
        });
    }
  };

  // Load listings
  const loadListings = async (newFilters = filters, page = 1) => {
    setLoading(true);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const filtered = filterListings(mockListings, newFilters);
    const sorted = sortListings(filtered, newFilters?.sortBy);
    
    // Simulate pagination
    const itemsPerPage = 6;
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedResults = sorted?.slice(0, endIndex);
    
    setListings(paginatedResults);
    setHasMore(endIndex < sorted?.length);
    setCurrentPage(page);
    setLoading(false);
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters)?.forEach(([key, value]) => {
      if (value && value !== 'all' && value !== false) {
        params?.set(key, value?.toString());
      }
    });
    setSearchParams(params);
    
    // Reset pagination and load new results
    setCurrentPage(1);
    loadListings(newFilters, 1);
  };

  // Handle load more
  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    const filtered = filterListings(mockListings, filters);
    const sorted = sortListings(filtered, filters?.sortBy);
    
    const itemsPerPage = 6;
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const newItems = sorted?.slice(startIndex, endIndex);
    
    setListings(prev => [...prev, ...newItems]);
    setHasMore(endIndex < sorted?.length);
    setCurrentPage(nextPage);
  };

  // Handle clear search
  const handleClearSearch = () => {
    const clearedFilters = {
      ...filters,
      query: ''
    };
    handleFiltersChange(clearedFilters);
  };

  // Initial load
  useEffect(() => {
    loadListings(filters, 1);
  }, []);

  const filteredCount = filterListings(mockListings, filters)?.length;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <SearchHeader
          searchQuery={filters?.query}
          resultCount={filteredCount}
          filters={filters}
          onClearSearch={handleClearSearch}
          onToggleFilters={() => setShowMobileFilters(!showMobileFilters)}
          showMobileFilters={showMobileFilters}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            {/* Filter Panel */}
            <div className="lg:col-span-3">
              <FilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
                isOpen={showMobileFilters}
                onToggle={() => setShowMobileFilters(!showMobileFilters)}
                resultCount={filteredCount}
              />
            </div>
            
            {/* Results Grid */}
            <div className="lg:col-span-9 mt-8 lg:mt-0">
              <ResultsGrid
                listings={listings}
                loading={loading}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SearchResults;